// اللَّهُ لَا إِلَٰهَ إِلَّا هُوَ الْحَيُّ الْقَيُّومُ ۚ لَا تَأْخُذُهُ سِنَةٌ وَلَا نَوْمٌ ۚ
// لَهُ مَا فِي السَّمَاوَاتِ وَمَا فِي الْأَرْضِ ۗ مَنْ ذَا الَّذِي يَشْفَعُ عِنْدَهُ إِلَّا بِإِذْنِهِ ۚ
// يَعْلَمُ مَا بَيْنَ أَيْدِيهِمْ وَمَا خَلْفَهُمْ ۖ وَلَا يُحِيطُونَ بِشَيْءٍ مِنْ عِلْمِهِ إِلَّا بِمَا شَاءَ ۚ
// وَسِعَ كُرْسِيُّهُ السَّمَاوَاتِ وَالْأَرْضَ ۖ وَلَا يَئُودُهُ حِفْظُهُمَا ۚ وَهُوَ الْعَلِيُّ الْعَظِيمُ
// (Bakara 255)

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/**
 * VS Code / Visual Studio tarzi sayfa tab sistemi.
 * Her acilan sayfa bir tab olur, kullanici sol menu yerine bu tab'lardan gezer.
 * Tab'lar sessionStorage'da persist — refresh sonrasi aynen durur.
 */
export interface PageTab {
  readonly id: string;         // path ile ayni (unique)
  readonly path: string;       // React Router path (sorgu parametreli olabilir)
  label: string;               // Tab basligi
  icon?: string;               // Lucide icon adi
  pinned?: boolean;            // Sag tiklayip pinledikten sonra kapatma X gostermez
  dirty?: boolean;             // Form dokunulmus mu — kapatirken uyarir
  // Sayfanın kendi bildirdiği başlık (iç görünüme göre: e-Fatura formu e-Arşiv'e geçince "Yeni e-Arşiv").
  // Adresten türetilen etiketi ezer; adres değiştirilmez çünkü yeni anahtar formu yeniden kurar.
  pageLabel?: string;
  // Bu sekmeyi açan sekme. Kapatınca kullanıcı komşu sekmeye değil GELDİĞİ sekmeye döner
  // (bağlı belgeden açılan irsaliye kapanınca teklife dönülür).
  openerId?: string;
}

// Sorgu parametresi SAYFANIN İÇ GÖRÜNÜMÜ olan yollar: kutunun kanal sekmesi (?c=) ve taslak
// görünümü (?v=) aynı sayfada değişir. Sekme tam adrese göre tanındığında her kanal tıklaması
// yeni bir "Giden Kutusu" sekmesi açıyordu (30.09.2026, kullanıcı: "her tıklamada bir sekme
// daha olamaz"). Bu yollarda sekme yalnız pathname ile tanınır; path son görünümü hatırlar.
const SINGLE_TAB_PATHS = new Set(['/eportal/outbox', '/eportal/inbox']);

export function isSingleTabPath(fullPath: string): boolean {
  return SINGLE_TAB_PATHS.has(fullPath.split('?')[0] ?? fullPath);
}

export function tabKeyFor(fullPath: string): string {
  return isSingleTabPath(fullPath) ? (fullPath.split('?')[0] ?? fullPath) : fullPath;
}

// Eski oturumdan kalan sekmeler tam adresle anahtarlanmış olabilir; aynı anahtara düşenlerden
// sonuncusu (en son görülen görünüm) kalır.
function collapseTabs(tabs: PageTab[]): PageTab[] {
  const byKey = new Map<string, PageTab>();
  for (const t of tabs) byKey.set(tabKeyFor(t.path), { ...t, id: tabKeyFor(t.path) });
  return [...byKey.values()];
}

/** Etkin sekme kapanınca öne gelecek sekme: önce onu açan sekme (hâlâ açıksa), yoksa sağdaki komşu. */
export function tabAfterClose(tabs: readonly PageTab[], id: string): PageTab | undefined {
  const idx = tabs.findIndex((t) => t.id === id);
  const remaining = tabs.filter((t) => t.id !== id);
  const opener = tabs[idx]?.openerId;
  return remaining.find((t) => t.id === opener) ?? remaining[idx] ?? remaining[idx - 1] ?? remaining[0];
}

interface TabState {
  readonly tabs: PageTab[];
  readonly activeTabId: string | null;
  openTab: (tab: PageTab) => void;
  closeTab: (id: string) => void;
  closeOthers: (id: string) => void;
  closeAll: () => void;
  closeToRight: (id: string) => void;
  setActive: (id: string) => void;
  renameTab: (id: string, label: string, icon?: string) => void;
  setPageLabel: (id: string, label: string | undefined) => void;
  togglePinned: (id: string) => void;
  setDirty: (id: string, dirty: boolean) => void;
  reorderTabs: (from: number, to: number) => void;
  resetTabs: () => void;
}

export const useTabStore = create<TabState>()(
  persist(
    (set, get) => ({
      tabs: [],
      activeTabId: null,

      openTab: (tab) => {
        const { tabs, activeTabId } = get();
        const existing = tabs.find((t) => t.id === tab.id);
        if (existing) {
          // Tab zaten ayni etiket/iconla var ve zaten aktif — hic bir sey yapma.
          // Bu olmadan sonsuz render loop olusabilir (React error #185).
          const label = existing.pageLabel ?? tab.label;
          const sameLabel = existing.label === label;
          const sameIcon = (existing.icon ?? null) === (tab.icon ?? null);
          const sameActive = activeTabId === tab.id;
          const samePath = existing.path === tab.path;
          if (sameLabel && sameIcon && sameActive && samePath) return;
          set({
            tabs: sameLabel && sameIcon && samePath
              ? tabs
              : tabs.map((t) =>
                  t.id === tab.id ? { ...t, path: tab.path, label, icon: tab.icon ?? t.icon } : t,
                ),
            activeTabId: tab.id,
          });
          return;
        }
        const opened = activeTabId && activeTabId !== tab.id ? { ...tab, openerId: activeTabId } : tab;
        set({ tabs: [...tabs, opened], activeTabId: tab.id });
      },

      closeTab: (id) => {
        const { tabs, activeTabId } = get();
        const target = tabs.find((t) => t.id === id);
        if (target?.pinned) return;
        const remaining = tabs.filter((t) => t.id !== id);
        const newActive = activeTabId === id ? (tabAfterClose(tabs, id)?.id ?? null) : activeTabId;
        set({ tabs: remaining, activeTabId: newActive });
      },

      closeOthers: (id) => {
        const { tabs } = get();
        set({
          tabs: tabs.filter((t) => t.id === id || t.pinned),
          activeTabId: id,
        });
      },

      closeAll: () => {
        const { tabs } = get();
        const kept = tabs.filter((t) => t.pinned);
        set({ tabs: kept, activeTabId: kept[0]?.id ?? null });
      },

      closeToRight: (id) => {
        const { tabs, activeTabId } = get();
        const idx = tabs.findIndex((t) => t.id === id);
        if (idx < 0) return;
        const kept = tabs.filter((t, i) => i <= idx || t.pinned);
        const active = kept.find((t) => t.id === activeTabId)?.id ?? id;
        set({ tabs: kept, activeTabId: active });
      },

      setActive: (id) => {
        const { tabs, activeTabId } = get();
        if (activeTabId === id) return; // zaten aktif — set cagirip re-render tetikleme
        if (tabs.some((t) => t.id === id)) {
          set({ activeTabId: id });
        }
      },

      renameTab: (id, label, icon) => {
        set({
          tabs: get().tabs.map((t) => (t.id === id ? { ...t, label, ...(icon ? { icon } : {}) } : t)),
        });
      },

      setPageLabel: (id, label) => {
        const t = get().tabs.find((x) => x.id === id);
        if (!t || t.pageLabel === label) return;
        set({
          tabs: get().tabs.map((x) => (x.id === id ? { ...x, pageLabel: label, ...(label ? { label } : {}) } : x)),
        });
      },

      togglePinned: (id) => {
        set({
          tabs: get().tabs.map((t) => (t.id === id ? { ...t, pinned: !t.pinned } : t)),
        });
      },

      setDirty: (id, dirty) => {
        set({
          tabs: get().tabs.map((t) => (t.id === id ? { ...t, dirty } : t)),
        });
      },

      reorderTabs: (from, to) => {
        const { tabs } = get();
        if (from === to || from < 0 || to < 0 || from >= tabs.length || to >= tabs.length) return;
        const next = [...tabs];
        const [moved] = next.splice(from, 1);
        next.splice(to, 0, moved!);
        set({ tabs: next });
      },

      // Yeni giriş/çıkışta tüm sekmeleri sıfırla (pinned dahil) — eski oturum sekmeleri taşınmaz.
      resetTabs: () => set({ tabs: [], activeTabId: null }),
    }),
    {
      name: 'elpo-page-tabs',
      merge: (persisted, current) => {
        const saved = persisted as Partial<TabState> | undefined;
        if (!saved?.tabs) return current;
        const tabs = collapseTabs(saved.tabs);
        const activeTabId = saved.activeTabId ? tabKeyFor(saved.activeTabId) : null;
        return { ...current, tabs, activeTabId };
      },
      storage: {
        getItem: (name) => {
          const raw = sessionStorage.getItem(name);
          return raw ? JSON.parse(raw) : null;
        },
        setItem: (name, value) => sessionStorage.setItem(name, JSON.stringify(value)),
        removeItem: (name) => sessionStorage.removeItem(name),
      },
    },
  ),
);
